import { Trans, useLingui } from "@lingui/react/macro";
import type { DesktopUpdateState } from "@rakazo/contracts";
import { Button, toast } from "@rakazo/ui-web";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { desktopBridge } from "../lib/desktop";

const UPDATE_RESPONSE_TIMEOUT_MS = 30_000;

function useDesktopUpdates() {
  const { t } = useLingui();
  const bridge = desktopBridge()?.update;
  const [state, setState] = useState<DesktopUpdateState | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmedCheck, setConfirmedCheck] = useState<string | null>(null);
  const [checkFeedback, setCheckFeedback] = useState<{ checkedAt: string | null } | null>(null);
  const [waitingForCheck, setWaitingForCheck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = useRef(false);
  const revision = useRef(0);
  const checkRequest = useRef<{ checkedAt: string | null } | null>(null);
  const deadline = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const cancelAction = useRef<(() => void) | null>(null);

  function clearCheckWait() {
    checkRequest.current = null;
    clearTimeout(deadline.current);
    deadline.current = undefined;
    setWaitingForCheck(false);
  }

  function observeCheck(next: DesktopUpdateState, returnedFromAction = false) {
    const request = checkRequest.current;
    if (!request || next.phase === "checking") return;
    if (next.phase === "idle" && !next.message) {
      if (next.checkedAt && next.checkedAt !== request.checkedAt) {
        setConfirmedCheck(next.checkedAt);
        setCheckFeedback(null);
      } else if (returnedFromAction) {
        // A throttled native check returns the previous snapshot, not a new success.
        // Обмежена перевірка повертає попередній стан, а не новий успішний результат.
        setCheckFeedback({ checkedAt: next.checkedAt });
      } else return;
    }
    clearCheckWait();
  }

  useEffect(() => {
    if (!bridge) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    async function refresh() {
      const started = revision.current;
      try {
        const next = await bridge!.state();
        if (!stopped && started === revision.current && !pending.current) {
          setState((current) =>
            current &&
            Object.entries(next).every(
              ([key, value]) => current[key as keyof DesktopUpdateState] === value,
            )
              ? current
              : next,
          );
          observeCheck(next);
        }
      } catch {
        // A disappearing bridge during shutdown should not interrupt the app.
        // Зникнення мосту під час закриття не повинно переривати застосунок.
      } finally {
        if (!stopped) timer = setTimeout(refresh, 2_000);
      }
    }
    void refresh();
    return () => {
      stopped = true;
      revision.current++;
      checkRequest.current = null;
      clearTimeout(timer);
      clearTimeout(deadline.current);
      cancelAction.current?.();
      cancelAction.current = null;
      pending.current = false;
    };
  }, [bridge]);

  async function act(action: "check" | "install") {
    if (!bridge || pending.current || waitingForCheck) return;
    pending.current = true;
    const started = ++revision.current;
    setBusy(true);
    setError(null);
    setCheckFeedback(null);
    if (action === "check") {
      checkRequest.current = { checkedAt: state?.checkedAt ?? null };
      setWaitingForCheck(true);
    }
    try {
      const next = await new Promise<DesktopUpdateState>((resolve, reject) => {
        cancelAction.current = () => reject(new Error("Update response cancelled"));
        deadline.current = setTimeout(() => {
          if (started !== revision.current && !checkRequest.current) return;
          setError(
            action === "check"
              ? t`No update-check result was received. Try again.`
              : t`Could not complete the update. Try again.`,
          );
          clearCheckWait();
          cancelAction.current?.();
        }, UPDATE_RESPONSE_TIMEOUT_MS);
        void bridge[action]().then(resolve, reject);
      });
      if (started !== revision.current) return;
      setState(next);
      if (action === "check") observeCheck(next, true);
      else clearCheckWait();
      if (next.phase === "error" || action === "install") setError(next.message);
    } catch {
      if (started === revision.current) {
        setError(
          action === "check"
            ? t`Could not check for updates. Try again.`
            : t`Could not complete the update. Try again.`,
        );
        clearCheckWait();
      }
    } finally {
      if (started === revision.current) {
        revision.current++;
        pending.current = false;
        cancelAction.current = null;
        setBusy(false);
      }
    }
  }
  return { state, busy, error, act, confirmedCheck, checkFeedback, waitingForCheck };
}

const UpdatesContext = createContext<ReturnType<typeof useDesktopUpdates> | null>(null);

const DESKTOP_UPDATE_TOAST = "desktop-update";

export function DesktopUpdatesProvider({ children }: { children: ReactNode }) {
  const updates = useDesktopUpdates();
  const [dismissed, setDismissed] = useState<string | null>(null);
  const { state, error } = updates;
  useEffect(() => {
    if (error) setDismissed(null);
  }, [error]);
  const prompt = Boolean(
    state &&
      (state.phase === "ready" || (error && state.availableVersion)) &&
      dismissed !== state.availableVersion,
  );
  // Keep the toast up while a retry runs, so a quick failure reuses it instead of racing
  // its dismissal.
  const [shown, setShown] = useState(false);
  const visible = prompt || (shown && updates.busy);
  const note = error ?? state?.message ?? null;
  useEffect(() => {
    setShown(visible);
  }, [visible]);
  useEffect(() => {
    if (!visible) return;
    // The app's Toaster renders inside this provider, so the toast reads live state from context.
    // A custom toast is not sized by the toaster, so it takes the toast width itself; the toaster
    // measures a toast only when it is published, so a new message republishes it to be measured.
    toast.custom(() => <DesktopUpdatePrompt onLater={setDismissed} />, {
      id: DESKTOP_UPDATE_TOAST,
      className: "w-(--width)",
      duration: Number.POSITIVE_INFINITY,
      dismissible: false,
    });
    return () => {
      toast.dismiss(DESKTOP_UPDATE_TOAST);
    };
  }, [visible, note]);
  return <UpdatesContext.Provider value={updates}>{children}</UpdatesContext.Provider>;
}

function DesktopUpdatePrompt({ onLater }: { onLater: (version: string | null) => void }) {
  const { t } = useLingui();
  const updates = useContext(UpdatesContext);
  if (!updates?.state) return null;
  const { state, busy, error, act } = updates;
  return (
    <aside
      aria-label={t`Desktop update`}
      className="w-full rounded-xl border border-border bg-card p-3 text-card-foreground shadow-lg"
    >
      <div className="flex items-center gap-2">
        <Button
          disabled={busy}
          onClick={() => void act(state.phase === "ready" ? "install" : "check")}
        >
          {state.phase === "ready" ? (
            <Trans>Restart to update</Trans>
          ) : (
            <Trans>Check for updates</Trans>
          )}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={() => onLater(state.availableVersion)}>
          <Trans>Later</Trans>
        </Button>
      </div>
      {error || state.message ? (
        <p role="status" className="mt-2 text-sm">
          <UpdateMessage message={error ?? state.message ?? ""} />
        </p>
      ) : null}
    </aside>
  );
}

function UpdateMessage({ message }: { message: string }) {
  const { t } = useLingui();
  switch (message) {
    case "Could not reach the update server.":
      return <>{t`Could not reach the update server.`}</>;
    case "No desktop releases are published for this build yet.":
      return <>{t`No desktop releases are published for this build yet.`}</>;
    case "Automatic updates are turned off for this install.":
      return <>{t`Automatic updates are turned off for this install.`}</>;
    case "Automatic updates only run in an installed build.":
      return <>{t`Automatic updates only run in an installed build.`}</>;
    case "Automatic updates are not configured for this HrokBot build.":
      return <>{t`Automatic updates are not configured for this HrokBot build.`}</>;
    case "The update could not be completed. Try again later.":
      return <>{t`The update could not be completed. Try again later.`}</>;
    case "This update could not be verified. Reinstall HrokBot from a trusted download.":
      return (
        <>{t`This update could not be verified. Reinstall HrokBot from a trusted download.`}</>
      );
    default:
      return <>{message}</>;
  }
}

/** Reuse the provider's bridge polling and guarded install action. */
export function useSidebarUpdate() {
  const updates = useContext(UpdatesContext);
  return {
    supported: Boolean(desktopBridge()?.update),
    available: updates?.state?.phase === "ready",
    busy: updates?.busy ?? false,
    check: () => {
      void updates?.act("check");
    },
    install: () => {
      void updates?.act("install");
    },
  };
}

export function DesktopUpdateSection() {
  const { t, i18n } = useLingui();
  const updates = useContext(UpdatesContext);
  if (!updates?.state) return null;
  const { state, busy, error, act, confirmedCheck, checkFeedback, waitingForCheck } = updates;
  const ready = state.phase === "ready";
  const downloading = state.phase === "available" || state.phase === "downloading";
  const checking = busy || waitingForCheck || (state.phase === "checking" && !error);
  const checkedAt = state.checkedAt ? new Date(state.checkedAt) : null;
  const checkedTime =
    checkedAt && Number.isFinite(checkedAt.getTime())
      ? checkedAt.toLocaleTimeString(i18n.locale, { hour: "2-digit", minute: "2-digit" })
      : null;
  return (
    <section
      data-testid="desktop-update-settings"
      className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm"
    >
      <h3 className="text-[15px] font-medium text-foreground">
        <Trans>Desktop app</Trans>
      </h3>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-muted-foreground">v{state.currentVersion}</span>
        <Button
          variant="outline"
          className="min-h-11 rounded-full"
          disabled={checking || downloading}
          onClick={() => void act(ready ? "install" : "check")}
        >
          {ready ? (
            <Trans>Restart to update</Trans>
          ) : downloading ? (
            <Trans>Downloading…</Trans>
          ) : checking ? (
            <Trans>Checking…</Trans>
          ) : (
            <Trans>Check for updates</Trans>
          )}
        </Button>
      </div>
      <p
        role="status"
        className={`mt-3 text-sm ${error ? "text-destructive" : "text-muted-foreground"}`}
      >
        {error ? (
          <UpdateMessage message={error} />
        ) : checkFeedback &&
          state.phase === "idle" &&
          checkFeedback.checkedAt === state.checkedAt ? (
          t`A new check was not started. Try again in a minute.`
        ) : checking ? (
          t`Checking…`
        ) : state.message ? (
          <UpdateMessage message={state.message} />
        ) : ready ? (
          t`Version ${state.availableVersion} is ready to install.`
        ) : downloading ? (
          state.percent === null ? (
            t`Version ${state.availableVersion} is available. Preparing download…`
          ) : (
            t`Downloading version ${state.availableVersion}: ${state.percent}%`
          )
        ) : state.phase === "idle" && confirmedCheck && state.checkedAt === confirmedCheck ? (
          <Trans>Up to date</Trans>
        ) : null}
      </p>
      {checkedTime ? (
        <p className="mt-1 text-xs text-muted-foreground">
          <Trans>
            Last checked at <time dateTime={state.checkedAt ?? undefined}>{checkedTime}</time>
          </Trans>
        </p>
      ) : null}
    </section>
  );
}
